import React, { useRef, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { FontWeight } from '@clutter/clean';
import { useDebouncedValue } from '@clutter/hooks';

import {
  Estimation__ItemCategory,
  ItemCategorySearchDocument,
} from '@graphql/platform';

import { SelectedItems } from '@root/components/checkout/product_pages/subcomponents/plan_estimator_modal/types';
import { useTrack } from '@root/initializers/wt';

import plusTealIcon from '@images/checkout/challenger/plus_teal.svg';
import searchIcon from '@images/checkout/challenger/search.svg';

import { useChallengerBreakpoints } from './hooks/use_challenger_breakpoints';
import { MOBILE } from './media';
import { CHALLENGER as T } from './tokens';

/**
 * Challenger-only search for the storage calculator.
 * Behavior matches classic `plan_estimator_modal/search_bar.tsx`
 * (popular categories → itemCategorySearch → multi-select → Add N items).
 * Visuals stay on challenger tokens / Figma.
 */

/** Same seeds as classic PlanEstimatorModal SearchBar. */
const POPULAR_SEARCH_TERMS = [
  'Bed',
  'Mattress',
  'Appliance',
  'Sofa',
  'Desk',
  'Box',
  'Chair',
  'Table',
] as const;

const SearchWrap = styled.div`
  position: relative;
  z-index: 3;
  width: 100%;
  flex-shrink: 0;
`;

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[8]};
  width: 100%;
  height: 48px;
  padding: 0 ${T.space[16]};
  box-sizing: border-box;
  border: 1px solid ${T.color.borderDefault};
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};
`;

const SearchInput = styled.input`
  appearance: none;
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Regular};
  font-size: 16px;
  line-height: 24px;
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.3px;

    /* iOS Safari zooms inputs < 16px; Android can keep Figma 14px. */
    @supports (-webkit-touch-callout: none) {
      font-size: 16px;
      line-height: 24px;
      letter-spacing: normal;
    }
  }

  &::placeholder {
    color: ${T.color.textTertiary};
  }
`;

const SearchIconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  img {
    display: block;
    width: 18px;
    height: 18px;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow: hidden;
  border: 1px solid ${T.color.borderDefault};
  border-radius: ${T.radius.md};
  background: ${T.color.bgPrimary};
  box-shadow: 0 8px 24px rgba(4, 7, 17, 0.12);

  ${MOBILE} {
    /* Visual viewport height is already keyboard-adjusted — do not subtract inset again. */
    max-height: min(
      280px,
      max(140px, calc(var(--storage-calc-vv-height, 100dvh) - 180px))
    );
  }
`;

/** Grows only with results — does not stretch and leave a gap above Add. */
const ResultsScroll = styled.div`
  flex: 0 1 auto;
  overflow: auto;
  max-height: 220px;
`;

const ResultRow = styled.button<{ selected?: boolean }>`
  appearance: none;
  display: flex;
  align-items: center;
  gap: ${T.space[8]};
  width: 100%;
  padding: ${T.space[12]} ${T.space[16]};
  border: none;
  background: ${({ selected }) =>
    selected ? T.color.bgSelected : T.color.bgPrimary};
  cursor: pointer;
  text-align: left;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textPrimary};

  &:hover {
    background: ${T.color.bgSelected};
  }
`;

const DropdownFooter = styled.div<{ $placement?: 'top' | 'bottom' }>`
  flex: 0 0 auto;
  padding: ${T.space[12]} ${T.space[16]};

  ${({ $placement = 'bottom' }) =>
    $placement === 'top'
      ? css`
          border-bottom: 1px solid ${T.color.borderDefault};
        `
      : css`
          border-top: 1px solid ${T.color.borderDefault};
        `}
`;

/**
 * Full-width CTA under search results. Fixed height + matching line-height
 * keeps the label optically centered (Avenir Next sits high with flex-only).
 */
const AddItemsButton = styled.button`
  appearance: none;
  display: block;
  width: 100%;
  height: 40px;
  margin: 0;
  padding: 0 ${T.space[16]};
  border: none;
  border-radius: ${T.radius.sm};
  background: ${T.color.textBrand};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 40px;
  color: ${T.color.textInverse};
  text-align: center;
  box-sizing: border-box;
`;

const PopularPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[12]};
  width: 100%;
  padding: ${T.space[12]} ${T.space[16]};
  box-sizing: border-box;
`;

const PopularLabel = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textTertiary};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${T.space[8]};
`;

const Chip = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 37px;
  padding: ${T.space[8]} ${T.space[12]} ${T.space[8]} ${T.space[8]};
  border: 1px solid ${T.color.borderLight};
  border-radius: ${T.radius.pill};
  background: ${T.color.bgPrimary};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
  color: ${T.color.textPrimary};
  box-sizing: border-box;

  &:hover {
    border-color: ${T.color.borderBrand};
  }
`;

const ChipIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  img {
    display: block;
    width: 12px;
    height: 12px;
  }
`;

const EmptyMessage = styled.div`
  padding: ${T.space[16]};
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textTertiary};
`;

const RESULTS_LIST_ID = 'challenger-storage-calculator-results';

/** Keep search focused when tapping dropdown controls (touch fires blur before mousedown). */
const preventInputBlur = (event: React.PointerEvent) => {
  event.preventDefault();
};

type Props = {
  searchInputRef?: React.Ref<HTMLInputElement>;
  selectedItems: SelectedItems;
  onSelectedItemsChange(items: SelectedItems): void;
  query: string;
  onQueryChange(query: string): void;
  /** Blur search (dismiss software keyboard) after dropdown actions on mobile. */
  onDismissKeyboard?: () => void;
};

/**
 * Classic-equivalent search behavior with challenger chrome:
 * focus empty → popular chips; chip/type → itemCategorySearch results;
 * multi-select → Add N items (increments quantity if already selected).
 */
export const StorageCalculatorSearch: React.FC<Props> = ({
  searchInputRef,
  selectedItems,
  onSelectedItemsChange,
  query,
  onQueryChange,
  onDismissKeyboard,
}) => {
  const track = useTrack();
  const { isDesktop, isMobile } = useChallengerBreakpoints();
  const [active, setActive] = useState(false);
  const closeDropdownTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [pendingSelectedItems, setPendingSelectedItems] =
    useState<SelectedItems>(() => new Map());
  const debouncedQuery = useDebouncedValue(query);

  const cancelCloseDropdown = () => {
    if (closeDropdownTimeoutRef.current) {
      clearTimeout(closeDropdownTimeoutRef.current);
      closeDropdownTimeoutRef.current = undefined;
    }
  };

  const scheduleCloseDropdown = () => {
    cancelCloseDropdown();
    closeDropdownTimeoutRef.current = setTimeout(() => {
      setActive(false);
    }, 0);
  };

  const { data, loading } = useQuery(ItemCategorySearchDocument, {
    variables: { query: debouncedQuery },
    skip: !debouncedQuery,
  });
  const searchResults = data?.results;
  const isSearchPending = query.length > 0 && debouncedQuery !== query;

  const onChangeQuery = (value: string) => {
    track({
      container: 'item_inventory',
      value,
      objectName: 'item_inventory_search_bar',
      objectType: 'search_bar',
      action: 'keystroke',
    });
    onQueryChange(value);
    setPendingSelectedItems(new Map());
    cancelCloseDropdown();
    if (value.length > 0) {
      setActive(true);
    }
  };

  const onSelectPopularTerm = (term: string) => {
    setPendingSelectedItems(new Map());
    onQueryChange(term);
    setActive(true);
  };

  const onTogglePending = (
    category: Pick<Estimation__ItemCategory, 'id' | 'name' | 'cuft'>,
  ) => {
    const next = new Map(pendingSelectedItems);
    if (next.has(category.id)) next.delete(category.id);
    else next.set(category.id, { category, quantity: 1 });
    setPendingSelectedItems(next);
    if (!isDesktop) onDismissKeyboard?.();
  };

  /** Same merge rules as classic PlanEstimatorModal SearchBar. */
  const onAddSelectedItems = () => {
    const update: SelectedItems = new Map();

    selectedItems.forEach((item, id) => {
      if (!pendingSelectedItems.has(id)) {
        update.set(id, item);
      }
    });
    pendingSelectedItems.forEach(({ category }, id) => {
      update.set(id, {
        category,
        quantity: selectedItems.has(id)
          ? selectedItems.get(id)!.quantity + 1
          : 1,
      });
    });

    onSelectedItemsChange(update);
    onQueryChange('');
    setPendingSelectedItems(new Map());
    setActive(false);
    onDismissKeyboard?.();
  };

  // Popular chips need focus. Results stay while the query has text so
  // dismissing the keyboard (blur) does not hide the suggested list.
  const showPopularInDropdown = active && query.length === 0;
  const showResults = query.length > 0;
  const showDropdown = showPopularInDropdown || showResults;
  const pendingItemCount = pendingSelectedItems.size;

  const addItemsButton =
    pendingItemCount > 0 ? (
      <AddItemsButton type="button" onClick={onAddSelectedItems}>
        {pendingItemCount === 1
          ? 'Add 1 item'
          : `Add ${pendingItemCount} items`}
      </AddItemsButton>
    ) : null;

  return (
    <SearchWrap
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) return;
        if (query.length > 0) return;
        scheduleCloseDropdown();
      }}
    >
      <SearchField>
        <SearchIconBox>
          <img src={searchIcon} alt="" width={18} height={18} />
        </SearchIconBox>
        <SearchInput
          ref={searchInputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={RESULTS_LIST_ID}
          aria-autocomplete="list"
          placeholder="Search and add items"
          value={query}
          enterKeyHint="search"
          autoComplete="off"
          onChange={(event) => onChangeQuery(event.target.value)}
          onFocus={() => {
            cancelCloseDropdown();
            setActive(true);
          }}
          onClick={() => {
            cancelCloseDropdown();
            setActive(true);
          }}
        />
      </SearchField>

      {showDropdown && (
        <Dropdown id={RESULTS_LIST_ID} onPointerDown={preventInputBlur}>
          {showPopularInDropdown && (
            <PopularPanel>
              <PopularLabel>
                Try out one of these popular categories to get started:
              </PopularLabel>
              <ChipRow>
                {POPULAR_SEARCH_TERMS.map((term) => (
                  <Chip
                    key={term}
                    type="button"
                    onClick={() => onSelectPopularTerm(term)}
                  >
                    <ChipIcon>
                      <img src={plusTealIcon} alt="" width={12} height={12} />
                    </ChipIcon>
                    {term}
                  </Chip>
                ))}
              </ChipRow>
            </PopularPanel>
          )}

          {showResults && (
            <>
              {isMobile && addItemsButton && (
                <DropdownFooter $placement="top">
                  {addItemsButton}
                </DropdownFooter>
              )}
              <ResultsScroll>
                {isSearchPending || loading ? (
                  <EmptyMessage>Searching…</EmptyMessage>
                ) : searchResults?.length ? (
                  searchResults.map((result) => (
                    <ResultRow
                      key={result.id}
                      type="button"
                      selected={pendingSelectedItems.has(result.id)}
                      onClick={() => onTogglePending(result)}
                    >
                      + {result.name}
                    </ResultRow>
                  ))
                ) : (
                  <EmptyMessage>
                    We couldn’t find that item, please try another search term.
                  </EmptyMessage>
                )}
              </ResultsScroll>
              {!isMobile && addItemsButton && (
                <DropdownFooter>{addItemsButton}</DropdownFooter>
              )}
            </>
          )}
        </Dropdown>
      )}
    </SearchWrap>
  );
};
